import React, { useCallback, useEffect, useState } from "react";
import { Inbox, CheckCheck, Mail, MailOpen, Users } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { ukDateTime } from "../lib/dates";
import { useAuth } from "../context/AuthContext";
import { InlineTextReply } from "../components/InlineTextReply";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";

const InboxItem = ({ m, canReply, onChange }) => {
  const toggle = () => api.post(`/inbox/texts/${m.id}/read?read=${!m.read}`).then(onChange).catch(() => toast.error("Couldn't update"));
  return (
    <li data-testid={`inbox-item-${m.id}`} className={`${CARD} p-4 ${m.read ? "" : "border-l-4"}`} style={m.read ? {} : { borderLeftColor: "var(--sj-primary)" }}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold flex flex-wrap items-center gap-2">
            <span data-testid={`inbox-name-${m.id}`}>{m.name || (m.member_id ? "Member" : "Unknown number")}</span>
            {m.cell_reply && <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#FEF6D8", color: "#8a6a00" }}><Users className="h-3 w-3" /> Cell group reply</span>}
            {!m.read && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full text-white" style={{ background: "var(--sj-primary)" }}>New</span>}
          </div>
          <div className="text-xs" style={{ color: "var(--sj-text-3)" }}>{[m.phone, ukDateTime(m.created_at)].filter(Boolean).join(" · ")}</div>
        </div>
        <button data-testid={`inbox-toggle-read-${m.id}`} onClick={toggle} className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 border hover:bg-[#EDF2F4]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
          {m.read ? <><Mail className="h-3.5 w-3.5" /> Mark unread</> : <><MailOpen className="h-3.5 w-3.5" /> Mark read</>}
        </button>
      </div>
      <p className="mt-2 text-sm whitespace-pre-wrap break-words" data-testid={`inbox-body-${m.id}`}>{m.body}</p>
      {m.replies?.map((r, i) => (
        <div key={i} className="mt-2 ml-6 rounded-xl p-3 text-sm" style={{ background: "#E3F1EC" }}>
          <div className="text-xs" style={{ color: "var(--sj-text-3)" }}>{r.author} replied · {ukDateTime(r.created_at)}</div>
          <p className="whitespace-pre-wrap break-words">{r.body}</p>
        </div>
      ))}
      {canReply && <InlineTextReply endpoint={`/inbox/texts/${m.id}/reply`} testid={`inbox-reply-${m.id}`} onSent={onChange} />}
    </li>
  );
};

export default function ReplyInbox() {
  const { user } = useAuth();
  const canReply = !!(user?.caps?.is_admin || user?.caps?.is_pastor);
  const [unreadOnly, setUnreadOnly] = useState(true);
  const [data, setData] = useState(null);
  const [limit, setLimit] = useState(50);
  const load = useCallback(() => api.get(`/inbox/texts?unread_only=${unreadOnly}&limit=${limit}`).then(({ data }) => setData(data)).catch(() => toast.error("Couldn't load the inbox")), [unreadOnly, limit]);
  useEffect(() => { load(); }, [load]);
  const readAll = () => api.post("/inbox/texts/read-all").then(() => { toast.success("All marked as read"); load(); });
  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="reply-inbox-page">
      <div className="sj-page-hero flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight flex items-center gap-2"><Inbox className="h-7 w-7" style={{ color: "var(--sj-primary)" }} /> Reply Inbox</h1>
          <p style={{ color: "var(--sj-text-2)" }}>Every text sent to the church number. <span data-testid="inbox-unread-count">{data ? `${data.unread} unread` : ""}</span></p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="inline-flex rounded-full border p-0.5" style={{ borderColor: "var(--sj-border)" }}>
            {[[true, "Unread"], [false, "All"]].map(([v, l]) => (
              <button key={l} data-testid={`inbox-filter-${l.toLowerCase()}`} onClick={() => setUnreadOnly(v)} className="rounded-full px-4 py-1.5 text-sm font-semibold" style={unreadOnly === v ? { background: "var(--sj-primary)", color: "#fff" } : { color: "var(--sj-text-2)" }}>{l}</button>
            ))}
          </div>
          <button data-testid="inbox-read-all" onClick={readAll} disabled={!data?.unread} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold border disabled:opacity-50" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}><CheckCheck className="h-4 w-4" /> Mark all read</button>
        </div>
      </div>
      {data && !data.items.length && <div className={`${CARD} p-10 text-center text-sm`} style={{ color: "var(--sj-text-3)" }} data-testid="inbox-empty">{unreadOnly ? "You're all caught up — no unread replies." : "No texts received yet."}</div>}
      <ul className="space-y-3">{data?.items.map((m) => <InboxItem key={m.id} m={m} canReply={canReply} onChange={load} />)}</ul>
      {data && data.total > data.items.length && (
        <button data-testid="inbox-show-more" onClick={() => setLimit(Math.min(500, limit + 50))} className="w-full rounded-full py-2.5 text-sm font-semibold border" style={{ borderColor: "var(--sj-border)", color: "var(--sj-primary)" }}>Show more ({data.total - data.items.length} older)</button>
      )}
    </div>
  );
}
